import moduleManager from './ModuleManager.js'
import store from '../store'
import api from "@/vue/services/api";

class GameModule {
  constructor() {
    this.name = 'game'
    this.isEnabled = false
    this.gameState = {
      score: 0,
      level: 1,
      unlockedPoints: new Set(),
      variables: {}, // Store game variables like 'score', 'points_visited', etc.
      inventory: {}, // Store collected objects with their images and metadata
      tasks: {}, // Store assigned/completed tasks by id
      quiz_answers: {}, // Store quiz answers by point ID and question ID
      executedActions: new Set(), // Track which actions have been executed
      game_completions: {}, // Store completed games by gameId (always boolean)
    }
    this.pendingInventoryChanges = [] // Track inventory changes for batch modal display
  }

  // Called when module is enabled
  onEnable(tourSettings) {
    console.log('[GameModule] Game module enabled')
    console.log('[GameModule] Tour settings:', tourSettings)
    console.log('[GameModule] Tour settings modules array:', tourSettings?.modules)
    this.isEnabled = true
    
    // Initialize game state from tour settings
    if (tourSettings.game) {
      this.gameState = {
        score: tourSettings.game.initial_score || 0,
        level: tourSettings.game.initial_level || 1,
        unlockedPoints: new Set(tourSettings.game.unlocked_points || []),
        variables: tourSettings.game.initial_variables || {},
        inventory: tourSettings.game.initial_inventory || {},
      tasks: {},
      quiz_answers: {},
      executedActions: new Set(),
      game_completions: {}
    }
  }

    // Hydrate from persisted Vuex module state if available (overrides tour defaults)
    try {
      const persisted = store.getters['modules/getModuleState']?.('game')?.data
      if (persisted && Object.keys(persisted).length > 0) {
        console.log('[GameModule] Hydrating game state from persisted Vuex data')
        // Merge persisted values, preserving Set instances (already revived in store)
        this.gameState = {
          ...this.gameState,
          ...persisted,
          // Ensure Sets remain Sets if present
          unlockedPoints: persisted.unlockedPoints instanceof Set ? persisted.unlockedPoints : (this.gameState.unlockedPoints || new Set()),
          executedActions: persisted.executedActions instanceof Set ? persisted.executedActions : (this.gameState.executedActions || new Set())
        }
      }
    } catch (e) {
      console.warn('[GameModule] Failed to hydrate from persisted state', e)
    }
    
    console.log('[GameModule] Initial game state:', this.gameState)
    
    // Register event listeners
    console.log('[GameModule] About to register event listeners')
    this.registerEventListeners()
    console.log('[GameModule] Event listeners registered')
    
    // Store initial game state
    moduleManager.setModuleData('game', this.gameState)
    
    console.log('[GameModule] Module enabled and event listeners registered')
    console.log('[GameModule] Module state after onEnable:')
    console.log('  - isEnabled:', this.isEnabled)
    console.log('  - moduleManager.isModuleActive("game"):', moduleManager.isModuleActive('game'))
    console.log('  - moduleManager.getModuleState("game"):', moduleManager.getModuleState('game'))
    console.log('  - Event listeners:', moduleManager.getEventListeners())
    console.log('  - point:before_open listeners:', moduleManager.getEventListeners().get('point:before_open'))
  }

  // Tasks API when game module is active
  assignTask(task) {
    if (!this.isEnabled) return
    const id = (task?.id ?? task?.slug ?? task?.name)?.toString()
    if (!id) return
    this.gameState.tasks[id] = {
      id,
      title: task.title || task.name || id,
      description: task.description || '',
      status: 'assigned',
      ...task
    }
    moduleManager.setModuleData('game', this.gameState)
    moduleManager.dispatchEvent('game:tasks_updated', { tasks: this.gameState.tasks, changedId: id })
  }

  completeTask(taskId) {
    if (!this.isEnabled) return
    const id = taskId?.toString()
    if (!id || !this.gameState.tasks[id]) return
    this.gameState.tasks[id] = {
      ...this.gameState.tasks[id],
      status: 'completed',
      completedAt: new Date().toISOString()
    }
    moduleManager.setModuleData('game', this.gameState)
    moduleManager.dispatchEvent('game:tasks_updated', { tasks: this.gameState.tasks, changedId: id })
  }

  getTasks() {
    return this.gameState.tasks || {}
  }

  // Called when module is disabled
  onDisable() {
    console.log('[GameModule] Game module disabled')
    this.isEnabled = false
    
    // Remove event listeners
    this.removeEventListeners()
  }

  registerEventListeners() {
    console.log('[GameModule] Registering event listeners')
    
    // Listen for point opening events
    moduleManager.addEventListener('point:opening', this.handlePointOpening.bind(this), 'game')
    console.log('[GameModule] Registered listener for point:opening')
    
    // Listen for point completion events
    moduleManager.addEventListener('point:completed', this.handlePointCompleted.bind(this), 'game')
    console.log('[GameModule] Registered listener for point:completed')
    
    // Listen for quiz submission events
    moduleManager.addEventListener('quiz:submitted', this.handleQuizSubmitted.bind(this), 'game')
    console.log('[GameModule] Registered listener for quiz:submitted')
    
    // Listen for points data loaded
    moduleManager.addEventListener('points:loaded', this.handlePointsLoaded.bind(this), 'game')
    console.log('[GameModule] Registered listener for points:loaded')
    
    // Listen for point:before_open events
    console.log('[GameModule] About to register listener for point:before_open')
    moduleManager.addEventListener('point:before_open', this.handlePointBeforeOpen.bind(this), 'game')
    console.log('[GameModule] Registered listener for point:before_open')
    
    console.log('[GameModule] All event listeners registered')
    console.log('[GameModule] Current event listeners:', moduleManager.getEventListeners())
    console.log('[GameModule] point:before_open listeners after registration:', moduleManager.getEventListeners().get('point:before_open'))
  }

  removeEventListeners() {
    moduleManager.removeEventListener('point:opening', this.handlePointOpening.bind(this), 'game')
    moduleManager.removeEventListener('point:completed', this.handlePointCompleted.bind(this), 'game')
    moduleManager.removeEventListener('quiz:submitted', this.handleQuizSubmitted.bind(this), 'game')
    moduleManager.removeEventListener('points:loaded', this.handlePointsLoaded.bind(this), 'game')
    moduleManager.removeEventListener('point:before_open', this.handlePointBeforeOpen.bind(this), 'game')
  }

  // Handle points data loaded from API
  handlePointsLoaded(data) {
    console.log('[GameModule] Points data loaded:', data)
    
    if (data.points) {
      // For now, just store the points data for later use
      // We'll handle conditions and actions when individual TourPoints are opened
      console.log('[GameModule] Points data stored for later processing')
    }
  }

  // Ensure all points without conditions are unlocked
  ensureUnconditionalPointsUnlocked(points) {
    console.log('[GameModule] Ensuring unconditional points are unlocked')
    
    // For now, don't process points in the overview
    // We'll handle this when individual TourPoints are opened
    console.log('[GameModule] Skipping point processing in overview')
  }

  // Process point conditions to determine if it should be unlocked
  processPointConditions(pointData) {
    console.log('[GameModule] Processing point conditions - this should only be called for TourPoints')
    
    // This method should only be called when a TourPoint is opened
    // For now, just log that we're processing
    const pointId = pointData.data?.point?.id || pointData.point?.id
    if (pointId) {
      console.log(`[GameModule] Processing conditions for TourPoint ${pointId}`)
    }
  }

  // Execute actions for a point
  executeActions(pointData) {
    const pointId = pointData.data?.point?.id || pointData.point?.id
    if (!pointId) return
    
    console.log(`[GameModule] Executing actions for point ${pointId}`)
    
    // Get actions from point data - check different possible locations
    let actions = []
    
    if (pointData.data?.actions) {
      actions = pointData.data.actions
      console.log(`[GameModule] Actions found in pointData.data.actions:`, actions)
    } else if (pointData.actions) {
      actions = pointData.actions
      console.log(`[GameModule] Actions found in pointData.actions:`, actions)
    } else if (pointData.data?.tour_point?.actions) {
      actions = pointData.data.tour_point.actions
      console.log(`[GameModule] Actions found in pointData.data.tour_point.actions:`, actions)
    } else {
      console.log(`[GameModule] No actions found in any expected location for point ${pointId}`)
    }
    
    console.log(`[GameModule] Actions found:`, actions)
    
    actions.forEach(action => {
      this.executeAction(action)
    })
    
    // Update module data after actions
    moduleManager.setModuleData('game', this.gameState)
  }

  // Execute a single action
  executeAction(action, pointId = null) {
    console.log(`[GameModule] Executing action:`, action)
    
    // Handle new API action structure
    if (typeof action === 'object' && action.type) {
      this.executeApiAction(action, pointId)
      return
    }
    
    // Handle old string-based action format for backward compatibility
    // if (typeof action === 'string') {
    //   this.executeStringAction(action)
    //   return
    // }
    
    console.warn(`[GameModule] Unknown action format:`, action)
  }

  // Execute API-style action object
  executeApiAction(action, pointId = null) {
    console.log(`[GameModule] Executing API action:`, action)
    
    const { type, operation, value, trigger } = action
    
    // Handle variable type actions
    if (type === 'variable') {
      this.executeVariableAction(action, pointId)
      return
    }

    // Handle object type actions
    if (type === 'object') {
      this.executeObjectAction(action, pointId)
      return
    }
    
    // Handle task type actions
    if (type === 'task') {
      this.executeTaskAction(action, pointId)
      return
    }
    
    // Handle other action types in the future
    console.warn(`[GameModule] Unknown action type: ${type}`)
  }

  // Execute task action (assign or complete)
  executeTaskAction(action, pointId = null) {
    console.log(`[GameModule] Executing task action:`, action)
    
    // Extract task id and data from possible locations (mirror object action style)
    let taskId = null
    let taskData = null
    
    if (action.taskId) {
      taskId = action.taskId
      taskData = action.taskData
    } else if (action.target?.id) {
      taskId = action.target.id.toString()
      taskData = action.target
    } else if (action.data?.type?.id) {
      taskId = action.data.type.id.toString()
      taskData = action.data.type
    } else if (action.data?.taskId) {
      taskId = action.data.taskId
      taskData = action.data.taskData
    }
    
    const operation = action.operation || action.data?.operation || 'set'
    const valueRaw = action.value ?? action.data?.value
    const value = typeof valueRaw === 'string' ? valueRaw : (valueRaw?.status || 'assigned')
    
    if (!taskId) {
      console.warn(`[GameModule] Task action missing taskId:`, action)
      return
    }
    
    // Normalize status from operation/value
    let status = 'assigned'
    if (operation === 'complete') status = 'completed'
    else if (operation === 'assign') status = 'assigned'
    else if (operation === 'set') status = (value === 'completed' || value === 'assigned') ? value : 'assigned'


      console.log(status);
    // Prepare task record
    const existing = this.gameState.tasks[taskId]
    const record = {
      id: taskId,
      title: taskData?.title || taskData?.name || existing?.title || taskId,
      description: taskData?.description || existing?.description || '',
      status,
      updatedAt: new Date().toISOString(),
      lastChangedFromPoint: pointId,
      ...taskData,
      ...(existing || {})
    }
    
    if (status === 'completed') {
      record.completedAt = new Date().toISOString()
    } else {
      delete record.completedAt

    }

    // update status
    record.status = status;


    console.log(record);
    // Save and emit
    this.gameState.tasks[taskId] = record
    moduleManager.setModuleData('game', this.gameState)
    moduleManager.dispatchEvent('game:tasks_updated', { tasks: this.gameState.tasks, changedId: taskId, status })
    
    console.log(`[GameModule] Task ${taskId} set to status: ${status}`)
  }

  async executeConnectboxCommandAction(action, pointId = null) {
    console.log(`[GameModule] Executing connectbox command action:`, action)



    if (!action.target?.id) {
      console.log("Command ID not found");
      return
    }

    try {
      // Send the connectbox message using the API service
      const response = await api.sendConnectboxCommand(
          action.target?.id
      )

    } catch (error) {
      console.error('Error sending connectbox message:', error)
    }

  }

  // Execute variable action
  executeVariableAction(action, pointId = null) {
    // Support both top-level and nested .data
    const operation = action.operation || action.data?.operation;
    const value = action.value || action.data?.value;
    const trigger = action.trigger || action.data?.trigger;
    const variableSlug = 
      (action.target_type === 'variable' ? action.target?.slug : action.target?.type) || 
      action.target?.slug || 
      action.target?.name || // fallback for backward compatibility
      action.target_type || 
      'unknown';


    let actionValue= null;
    if (action.target?.data_type === 'integer') {
        actionValue = parseFloat(value) || 0;
    } else {
        actionValue = value;
    }

    console.log(`[GameModule] Variable action: ${variableSlug} ${operation} ${actionValue}`);

    // Get current value
    let currentValue = this.getVariableValue(variableSlug) ?? (action.target?.data_type === 'integer' ? 0 : null);
      if (action.target?.data_type === 'integer') {
          currentValue = parseInt(currentValue);
      }
    console.log(`[GameModule] Current ${variableSlug}: ${currentValue}`);

    // Execute operation
    let newValue = currentValue;
    switch (operation) {
      case 'increase':
        newValue = currentValue + actionValue;
        break;
      case 'decrease':
        newValue = currentValue - actionValue;
        break;
      case 'multiply':
        newValue = currentValue * actionValue;
        break;
      case 'divide':
        newValue = currentValue / actionValue;
        break;
      case 'set':
        newValue = actionValue;
        break;
      default:
        console.warn(`[GameModule] Unknown operation: ${operation}`);
        return;
    }

    // Handle trigger if needed
    if (trigger === 'every_time') {
      console.log(`[GameModule] Action triggered every time`);
      // Update the variable
      this.setVariableValue(variableSlug, newValue);
      console.log(`[GameModule] New ${variableSlug}: ${newValue}`);
    } else if (trigger === 'first_time') {
      // Create a unique action identifier that includes the TourPoint ID
      const actionId = pointId ? `${pointId}_${variableSlug}_${operation}_${value}` : `${variableSlug}_${operation}_${value}`;
      
      // Check if this specific action has been executed before for this TourPoint
      const actionExecuted = this.gameState.executedActions.has(actionId);
      
      if (!actionExecuted) {
        console.log(`[GameModule] Action triggered first time - action ${actionId} was new for TourPoint ${pointId || 'unknown'}`);
        // Update the variable since this is the first time this action is being executed for this TourPoint
        this.setVariableValue(variableSlug, newValue);
        // Mark this action as executed for this TourPoint
        this.gameState.executedActions.add(actionId);
        console.log(`[GameModule] New ${variableSlug}: ${newValue}`);
        console.log(`[GameModule] Action ${actionId} marked as executed for TourPoint ${pointId || 'unknown'}`);
      } else {
        console.log(`[GameModule] Action skipped - action ${actionId} already executed for TourPoint ${pointId || 'unknown'}`);
        // Don't update the variable since this action was already executed for this TourPoint
        return;
      }
    } else {
      // Default behavior for unknown triggers
      console.log(`[GameModule] Unknown trigger: ${trigger}, executing anyway`);
      this.setVariableValue(variableSlug, newValue);
      console.log(`[GameModule] New ${variableSlug}: ${newValue}`);
    }
  }

  // Execute object action
  executeObjectAction(action, pointId = null) {
    console.log(`[GameModule] Executing object action:`, action)
    
    // Extract objectId from the correct location in the API structure
    let objectId = null
    let objectData = null
    
    // Check different possible locations for objectId - match TourPoint.vue logic
    if (action.objectId) {
      objectId = action.objectId
      objectData = action.objectData
    } else if (action.target?.id) {
      objectId = action.target.id.toString() // Convert to string for consistency
      objectData = action.target
    } else if (action.data?.type?.id) {
      objectId = action.data.type.id.toString() // Convert to string for consistency
      objectData = action.data.type
    } else if (action.data?.objectId) {
      objectId = action.data.objectId
      objectData = action.data.objectData
    }
    
    const operation = action.operation || action.data?.operation
    
    if (!objectId) {
      console.warn(`[GameModule] Object action missing objectId:`, action)
      return
    }

    let valueRaw = action.value ?? action.data?.value
    let value = parseInt(valueRaw)
    if (Number.isNaN(value)) {
      value = 1
    }

    console.log(`[GameModule] Object action details:`, {
      operation,
      objectId,
      objectData,
      pointId,
      value
    })
    
    // Map API operations to inventory operations
    let inventoryOperation = operation
    if (operation === 'increase') {
      inventoryOperation = 'add'
    } else if (operation === 'decrease') {
      inventoryOperation = 'remove'
    } else if (operation === 'set') {
      // For 'set' operation, check the value to determine if it's add or remove
      if (value >= 1) {
        inventoryOperation = 'add'
      } else if (value === 0) {
        inventoryOperation = 'remove'
      } else {
        console.warn(`[GameModule] Unknown set value for object action: ${value}`)
        return
      }
    }
    
    console.log(`[GameModule] Mapped operation: ${operation} -> ${inventoryOperation}`)
    
    // Track the change for batch display
    const changeData = {
      operation: inventoryOperation,
      objectId,
      objectData,
      pointId,
      quantity: value // Store the quantity that will be added/removed
    }

    // For remove operations, get the current item data from inventory before removal
    if (inventoryOperation === 'remove') {
      const currentItem = this.getInventoryItem(objectId)
      if (currentItem) {
        changeData.currentItemData = { ...currentItem }
      }
    }

    // Add to pending changes
    this.pendingInventoryChanges.push(changeData)
    console.log(`[GameModule] Added change to pending inventory changes. Total pending: ${this.pendingInventoryChanges.length}`)
    console.log(`[GameModule] Change added:`, changeData)
    
    switch (inventoryOperation) {
      case 'add':
        this.addObjectToInventory(objectId, objectData, pointId, false, value) // Don't show individual modal, pass the actual value as quantity
        break
      case 'remove':
        this.removeObjectFromInventory(objectId, pointId, false, value) // Don't show individual modal
        break
      default:
        console.warn(`[GameModule] Unknown object operation: ${operation} (mapped to: ${inventoryOperation})`)
    }
    
    // Update module data after inventory change
    moduleManager.setModuleData('game', this.gameState)
    
    // Dispatch inventory update event
    moduleManager.dispatchEvent('game:inventory_updated', {
      inventory: this.gameState.inventory,
      operation: inventoryOperation,
      objectId,
      pointId
    })
    
    console.log(`[GameModule] Object action executed for point ${pointId || 'unknown'}`)
  }
  
  // Add object to inventory
  addObjectToInventory(objectId, objectData, pointId = null, showModal = true, quantity = 1) {
    console.log(`[GameModule] Adding object to inventory:`, { objectId, objectData, pointId, showModal, quantity })
    
    // Extract metadata from the API structure
    let name = objectId
    let description = ''
    let image = null
   // let quantity = 1 // Default quantity
    
    if (objectData) {
      // Handle API structure where data might be in different locations
      name = objectData.name || objectData.title || objectId
      description = objectData.description || ''
      
      // Extract image URL from different possible locations
      if (objectData.preview_url) {
        image = objectData.preview_url
      } else if (objectData.image) {
        image = objectData.image
      } else if (objectData.url) {
        image = objectData.url
      }
    }
    
    // Get current quantity before adding
    const currentQuantity = parseInt(this.gameState.inventory[objectId]?.quantity || 0)
    const newQuantity = currentQuantity + parseInt(quantity)
    
    // Check if object already exists in inventory
    if (this.gameState.inventory[objectId]) {
      console.log(`[GameModule] Object ${objectId} already in inventory, updating quantity`)
      // Update quantity if item already exists
      this.gameState.inventory[objectId].quantity = newQuantity
    } else {
      console.log(`[GameModule] Object ${objectId} not in inventory, adding new item`)
      // Add new object to inventory
      this.gameState.inventory[objectId] = {
        id: objectId,
        name: name,
        description: description,
        image: image,
        quantity: newQuantity,
        collectedAt: new Date().toISOString(),
        collectedFromPoint: pointId,
        ...objectData // Include any additional metadata
      }
    }
    console.log(`[GameModule] Object ${objectId} added to inventory. Quantity: ${newQuantity}`)
    console.log(`[GameModule] Object details:`, this.gameState.inventory[objectId])
    
    // Show inventory change modal only if requested
    if (showModal) {
      this.showInventoryChangeModal({
        operation: 'add',
        item: this.gameState.inventory[objectId],
        quantity: quantity,
        newQuantity: newQuantity,
        previousQuantity: currentQuantity
      })
    }
  }
  
  // Remove object from inventory
  removeObjectFromInventory(objectId, pointId = null, showModal = true, quantity = 1) {
    console.log(`[GameModule] Removing object from inventory:`, { objectId, pointId, showModal, quantity })
    
    if (!this.gameState.inventory[objectId]) {
      console.warn(`[GameModule] Object ${objectId} not found in inventory`)
      return
    }
    
    // Get current quantity before removing
    const currentQuantity = this.gameState.inventory[objectId].quantity
    const itemData = { ...this.gameState.inventory[objectId] }
    
    // Decrease quantity
    let removeQuantity = parseInt(quantity)
    if (Number.isNaN(removeQuantity) || removeQuantity <= 0) {
      // If quantity is 0 or invalid, interpret as remove all
      removeQuantity = currentQuantity
    }
    this.gameState.inventory[objectId].quantity -= removeQuantity
    
    const newQuantity = this.gameState.inventory[objectId].quantity
    
    console.log(`[GameModule] Object ${objectId} quantity decreased to: ${newQuantity}`)
    
    // Remove object completely if quantity reaches 0 or below
    if (this.gameState.inventory[objectId].quantity <= 0) {
      delete this.gameState.inventory[objectId]
      console.log(`[GameModule] Object ${objectId} removed from inventory (quantity reached 0)`)
    }
    
    console.log(`[GameModule] Total unique items in inventory: ${Object.keys(this.gameState.inventory).length}`)
    
    // Show inventory change modal only if requested
    if (showModal) {
      this.showInventoryChangeModal({
        operation: 'remove',
        item: itemData,
        quantity: removeQuantity,
        newQuantity: newQuantity,
        previousQuantity: currentQuantity
      })
    }
  }
  
  // Show inventory change modal
  showInventoryChangeModal(changeData) {
    console.log(`[GameModule] Showing inventory change modal:`, changeData)
    
    // Create the modal data
    const modalData = {
      changes: [changeData]
    }
    
    // Determine the modal title and message based on the operation
    let title, message
    
    if (changeData.operation === 'add') {
      title = 'Item toegevoegd'
      message = `Je hebt een nieuw item verzameld!`  
    } else {
      title = 'Item verwijderd'
      message = `Een item is verwijderd uit je inventaris.`
    }
    
    // Dispatch event to show the modal
    // We'll use a custom event that the Vue app can listen to
    const event = new CustomEvent('show-inventory-modal', {
      detail: {
        title,
        message,
        type: 'inventory_change',
        data: modalData
      }
    })
    
    window.dispatchEvent(event)
    
    console.log(`[GameModule] Inventory change modal event dispatched`)
  }
  
  // Show batch inventory changes modal
  showBatchInventoryChanges() {
    console.log(`[GameModule] Showing batch inventory changes:`, this.pendingInventoryChanges)
    console.log(`[GameModule] Pending changes count: ${this.pendingInventoryChanges.length}`)
    
    if (this.pendingInventoryChanges.length === 0) {
      console.log(`[GameModule] No inventory changes to show`)
      return
    }
    
    // Get current inventory state to calculate new quantities
    const currentInventory = this.getInventory()
    
    // Prepare changes data for the modal
    const changes = this.pendingInventoryChanges.map(change => {
      if (change.operation === 'remove') {
        // For remove operations, the item might have been deleted from inventory
        // So we need to handle both cases: item still exists or item was removed
        let item = currentInventory[change.objectId]
        let newQuantity = 0
        let previousQuantity = 0
        
        if (!item) {
          // Item was completely removed, create a placeholder item for the modal
          // Use stored currentItemData if available, otherwise fall back to objectData
          const itemData = change.currentItemData || change.objectData
          item = {
            id: change.objectId,
            name: itemData?.name || change.objectId,
            description: itemData?.description || '',
            image: itemData?.image || null,
            quantity: 0
          }
          newQuantity = 0
          previousQuantity = change.currentItemData?.quantity || change.quantity || 1 // Use actual previous quantity if available
        } else {
          // Item still exists, calculate quantities normally
          newQuantity = item.quantity
          const removedQuantity = change.quantity || 1
          previousQuantity = item.quantity + removedQuantity // Remove operation decreases by the removed quantity
        }
        
        return {
          operation: change.operation,
          item: item,
          quantity: change.quantity || 1, // Use the actual quantity that was removed
          newQuantity: newQuantity,
          previousQuantity: previousQuantity
        }
      } else if (change.operation === 'add') {
        // For add operations, item should exist in current inventory
        const item = currentInventory[change.objectId]
        if (!item) {
          console.warn(`[GameModule] Item ${change.objectId} not found in inventory after add operation`)
          return null
        }
        
        // Calculate the quantity that was actually added
        // We need to get the quantity from the change data or calculate it
        const addedQuantity = change.quantity || 1 // Default to 1 if not specified
        const newQuantity = item.quantity
        const previousQuantity = newQuantity - addedQuantity
        
        return {
          operation: change.operation,
          item: item,
          quantity: addedQuantity, // The actual quantity that was added
          newQuantity: newQuantity,
          previousQuantity: previousQuantity
        }
      }
      
      return null
    }).filter(Boolean) // Remove null entries
    
    if (changes.length === 0) {
      console.log(`[GameModule] No valid changes to show`)
      return
    }
    
    // Create modal data
    const modalData = {
      changes: changes
    }
    
    // Determine title and message
    const addCount = changes.filter(c => c.operation === 'add').length
    const removeCount = changes.filter(c => c.operation === 'remove').length
    
    let title, message
    if (addCount > 0 && removeCount === 0) {
      title = `Item${addCount > 1 ? 's' : ''} verzameld`
      message = `Je hebt ${addCount} nieuw${addCount > 1 ? 'e' : ''} item${addCount > 1 ? 's' : ''} verzameld!`
    } else if (removeCount > 0 && addCount === 0) {
      title = 'Items verwijderd'
      message = `${removeCount} item${removeCount > 1 ? 's' : ''} is verwijderd uit je inventaris.`
    } else {
      title = 'Inventaris bijgewerkt'
      message = `Je inventaris is bijgewerkt met ${addCount} toevoeging${addCount !== 1 ? 'en' : ''} en ${removeCount} verwijdering${removeCount !== 1 ? 'en' : ''}.`
    }
    
    // Dispatch event to show the modal
    const event = new CustomEvent('show-inventory-modal', {
      detail: {
        title,
        message,
        type: 'inventory_change',
        data: modalData
      }
    })
    
    window.dispatchEvent(event)
    
    console.log(`[GameModule] Batch inventory change modal event dispatched`)
    
    // Clear pending changes
    this.pendingInventoryChanges = []
  }

  // Clear pending inventory changes
  clearPendingInventoryChanges() {
    console.log(`[GameModule] Clearing pending inventory changes. Previous count: ${this.pendingInventoryChanges.length}`)
    if (this.pendingInventoryChanges.length > 0) {
      console.log(`[GameModule] Previous pending changes:`, this.pendingInventoryChanges)
    }
    this.pendingInventoryChanges = []
    console.log(`[GameModule] Pending inventory changes cleared`)
  }

  // Check if a variable is a system variable
  isSystemVariable(variable) {
    return ['score', 'name'].includes(variable)
  }

  getAllVariables() {
    return this.gameState.variables
  }

  // Get the value of a variable
  getVariableValue(variable) {
    // Handle system variables
    if (this.isSystemVariable(variable)) {
      switch (variable) {
        case 'score':
          return this.gameState.score
        case 'name':
          return this.gameState.name || null
        default:
          return null
      }
    }
    
    // Handle custom variables
    return this.gameState.variables[variable] || null
  }

  // Set the value of a variable
  setVariableValue(variable, value) {
    // Handle system variables
    if (this.isSystemVariable(variable)) {
      switch (variable) {
        case 'score':
          this.gameState.score = value
          break
        case 'name':
          this.gameState.name = value
          break
        default:
          console.warn(`[GameModule] Unknown system variable: ${variable}`)
          return
      }
    } else {
      // Handle custom variables
      this.gameState.variables[variable] = value
    }
    
    // Update module data to ensure other components see the change
    moduleManager.setModuleData('game', this.gameState)
    
    // Dispatch game state update event
    moduleManager.dispatchEvent('game:state_updated', this.gameState)
  }

  // Handle point opening - check if point is locked
  handlePointOpening(data) {
    console.log('=== GAME MODULE HANDLE POINT OPENING ===')
    console.log('Received data:', data)
    
    const { pointId, pointData } = data
    
    console.log('Point ID:', pointId)
    console.log('Point data:', pointData)
    console.log('Actions in pointData.data?.actions:', pointData?.data?.actions)
    console.log('Actions in pointData.actions:', pointData?.actions)
    
    if (!this.isPointUnlocked(pointId)) {
      console.log(`[GameModule] Point ${pointId} is locked!`)
      
      // Get the conditions that are not met - look in different possible locations
      let conditions = []
      
      if (pointData.data?.conditions) {
        conditions = pointData.data.conditions
      } else if (pointData.conditions) {
        conditions = pointData.conditions
      } else if (pointData.data?.tour_point?.conditions) {
        conditions = pointData.data.tour_point.conditions
      }
      
      const unmetConditions = conditions.filter(condition => !this.evaluateCondition(condition))
      
      // Dispatch event to show lock message
      moduleManager.dispatchEvent('game:point_locked', {
        pointId,
        message: `This point is locked. Requirements: ${unmetConditions.join(', ')}`,
        unmetConditions,
        currentState: this.gameState
      })
      
      // Prevent point from opening
      return false
    }
    
    console.log(`[GameModule] Point ${pointId} is unlocked, allowing access`)
    
    // Execute actions when point is opened
    this.executeActions(pointData)
    
    return true
  }

  // Handle point completion
  handlePointCompleted(data) {
    const { pointId, score } = data
    
    console.log(`[GameModule] Point ${pointId} completed with score ${score}`)
    
    // Add score
    this.gameState.score += score || 10
    
    // Check for level up
    this.checkLevelUp()
    
    // Re-evaluate all point conditions after state change
    this.reevaluateAllPoints()
    
    // Update module data
    moduleManager.setModuleData('game', this.gameState)
    
    // Dispatch game state update
    moduleManager.dispatchEvent('game:state_updated', this.gameState)
  }

  // Handle quiz submission
  handleQuizSubmitted(data) {
    const { pointId, answers, correctAnswers } = data
    
    const score = this.calculateQuizScore(answers, correctAnswers)
    console.log(`[GameModule] Quiz submitted for point ${pointId}, score: ${score}`)
    
    // Add quiz score to game state
    this.gameState.score += score
    
    // Check for level up
    this.checkLevelUp()
    
    // Re-evaluate all point conditions after state change
    this.reevaluateAllPoints()
    
    // Update module data
    moduleManager.setModuleData('game', this.gameState)
    
    // Dispatch quiz completion event
    moduleManager.dispatchEvent('game:quiz_completed', {
      pointId,
      score,
      totalScore: this.gameState.score
    })
  }

  // Re-evaluate all points after state changes
  reevaluateAllPoints() {
    console.log('[GameModule] Re-evaluating all point conditions')
    
    // Get current points data from the store or component
    // This would need to be passed from the Points component
    moduleManager.dispatchEvent('game:request_points_data', {
      callback: (pointsData) => {
        if (pointsData) {
          Object.values(pointsData).forEach(pointData => {
            this.processPointConditions(pointData)
          })
        }
      }
    })
  }

  // Check if point is unlocked
  isPointUnlocked(pointId) {
    return this.gameState.unlockedPoints.has(pointId)
  }

  // Check for level up
  checkLevelUp() {
    const newLevel = Math.floor(this.gameState.score / 100) + 1
    if (newLevel > this.gameState.level) {
      this.gameState.level = newLevel
      console.log(`[GameModule] Level up! Now level ${newLevel}`)
      
      // Dispatch level up event
      moduleManager.dispatchEvent('game:level_up', {
        newLevel,
        score: this.gameState.score
      })
    }
  }

  // Calculate quiz score
  calculateQuizScore(answers, correctAnswers) {
    if (!answers || !correctAnswers) return 0
    
    let correct = 0
    Object.keys(answers).forEach(questionId => {
      if (answers[questionId] === correctAnswers[questionId]) {
        correct++
      }
    })
    
    return Math.round((correct / Object.keys(correctAnswers).length) * 20)
  }

  // Public methods for other components to use
  getGameState() {
    return this.gameState
  }

  getScore() {
    return this.gameState.score
  }

  getLevel() {
    return this.gameState.level
  }

  getVariable(variableName) {
    return this.getVariableValue(variableName)
  }

  setVariable(variableName, value) {
    this.setVariableValue(variableName, value)
  }

  // Inventory helper methods
  getInventory() {
    return this.gameState.inventory
  }
  
  getInventoryItem(objectId) {
    return this.gameState.inventory[objectId]
  }
  
  hasInventoryItem(objectId) {
    return !!(this.gameState.inventory[objectId] && this.gameState.inventory[objectId].quantity > 0)
  }
  
  getInventoryItemQuantity(objectId) {
    return parseInt(this.gameState.inventory[objectId]?.quantity) || 0
  }
  
  getInventoryCount() {
    return Object.keys(this.gameState.inventory).length
  }
  
  getTotalInventoryItems() {
    return Object.values(this.gameState.inventory).reduce((total, item) => total + (item.quantity || 0), 0)
  }

  // Create sample test data for demonstration
  createSampleTestData() {
    console.log('[GameModule] Creating sample test data')
    
    const samplePoints = {
      'point1': {
        data: {
          point: { id: 'point1' },
          conditions: [], // No conditions - always unlocked
          actions: [
            // New API format
            {
              id: 1,
              type: 'variable',
              data: {
                type: { id: 1, name: 'score', image: [] },
                operation: 'increase',
                value: '5',
                trigger: 'every_time'
              },
              operation: 'increase',
              trigger: 'every_time',
              value: '5'
            }
          ]
        }
      },
      'point2': {
        data: {
          point: { id: 'point2' },
          conditions: ['score >= 5'], // Requires at least 5 points
          actions: [
            // New API format
            {
              id: 2,
              type: 'variable',
              data: {
                type: { id: 1, name: 'score', image: [] },
                operation: 'increase',
                value: '10',
                trigger: 'every_time'
              },
              operation: 'increase',
              trigger: 'every_time',
              value: '10'
            },
            {
              id: 3,
              type: 'variable',
              data: {
                type: { id: 2, name: 'test_var', image: [] },
                operation: 'increase',
                value: '1',
                trigger: 'every_time'
              },
              operation: 'increase',
              trigger: 'every_time',
              value: '1'
            }
          ]
        }
      },
      'point3': {
        data: {
          point: { id: 'point3' },
          conditions: ['score >= 15', 'test_var >= 1'], // Requires 15 points AND test_var >= 1
          actions: [
            {
              id: 4,
              type: 'variable',
              data: {
                type: { id: 3, name: 'level', image: [] },
                operation: 'increase',
                value: '1',
                trigger: 'every_time'
              },
              operation: 'increase',
              trigger: 'every_time',
              value: '1'
            }
          ]
        }
      },
      'point4': {
        data: {
          point: { id: 'point4' },
          conditions: ['level >= 2'], // Requires level 2 or higher
          actions: [
            {
              id: 5,
              type: 'variable',
              data: {
                type: { id: 1, name: 'score', image: [] },
                operation: 'increase',
                value: '20',
                trigger: 'every_time'
              },
              operation: 'increase',
              trigger: 'every_time',
              value: '20'
            },
            {
              id: 6,
              type: 'variable',
              data: {
                type: { id: 4, name: 'custom_achievement', image: [] },
                operation: 'set',
                value: '1',
                trigger: 'first_time'
              },
              operation: 'set',
              trigger: 'first_time',
              value: '1'
            }
          ]
        }
      }
    }
    
    // Process the sample points
    Object.values(samplePoints).forEach(pointData => {
      this.processPointConditions(pointData)
    })
    
    // Ensure all points without conditions are unlocked
    this.ensureUnconditionalPointsUnlocked(samplePoints)
    
    // Update module data
    moduleManager.setModuleData('game', this.gameState)
    
    console.log('[GameModule] Sample test data created and processed')
    return samplePoints
  }

  // Handle TourPoint opening - check conditions and execute actions
  handleTourPointOpening(tourPointData) {
    console.log('=== GAME MODULE HANDLE TOUR POINT OPENING ===')
    console.log('Tour point data:', tourPointData)
    
    const pointId = tourPointData.point?.id
    if (!pointId) {
      console.log('[GameModule] No point ID found in tour point data')
      return true // Allow opening if no point ID
    }
    
    console.log(`[GameModule] Checking conditions for TourPoint ${pointId}`)
    
    // Check if TourPoint has conditions
    const conditions = tourPointData.conditions || []
    console.log(`[GameModule] Conditions found:`, conditions)
    
    if (conditions.length === 0) {
      console.log(`[GameModule] TourPoint ${pointId} has no conditions - allowing access`)
      // No conditions, allow access (actions will be executed in TourPoint.vue)
      return true
    }
    
    // Check conditions using current shared variable values
    const allConditionsMet = conditions.every(condition => {
      const isMet = this.evaluateCondition(condition)
      console.log(`[GameModule] Condition "${condition?.target?.name}" for TourPoint ${pointId}: ${isMet}`)
      return isMet
    })
    
    if (allConditionsMet) {
      console.log(`[GameModule] TourPoint ${pointId} unlocked - all conditions met`)
      // Conditions met, allow access (actions will be executed in TourPoint.vue)
      return true
    } else {
      console.log(`[GameModule] TourPoint ${pointId} locked - conditions not met`)
      
      // Get unmet conditions for the lock message
      const unmetConditions = conditions.filter(condition => !this.evaluateCondition(condition))
      
      // Dispatch event to show lock message
      moduleManager.dispatchEvent('game:point_locked', {
        pointId,
        message: `This point is locked. Requirements: ${unmetConditions.join(', ')}`,
        unmetConditions,
        currentState: this.gameState
      })
      
      return false
    }
  }

  // Evaluate a single condition
  evaluateCondition(condition) {
    console.log(`[GameModule] Evaluating condition:`, condition)
    console.log(`[GameModule] Condition type:`, typeof condition)
    
    // Handle API condition object structure
    if (typeof condition === 'object' && condition !== null) {
      return this.evaluateApiCondition(condition)
    }
    
    // Handle string condition format (legacy/fallback)
    // if (typeof condition === 'string') {
    //   return this.evaluateStringCondition(condition)
    // }
    
    console.warn(`[GameModule] Unknown condition format:`, condition)
    return false
  }

  // Evaluate API condition object
  evaluateApiCondition(condition) {
    console.log(`[GameModule] Evaluating API condition:`, condition)
    
    // Check if it's a variable condition
    if (condition.target_type === 'variable') {
      const { target, operator, value } = condition
      const variableSlug = target?.slug || target?.name // fallback for backward compatibility
      
      if (!variableSlug || !operator || value === undefined) {
        console.warn(`[GameModule] Invalid API condition structure:`, condition)
        return false
      }
      
      // Get current variable value
      const currentValue = this.getVariableValue(variableSlug) ?? (target?.data_type === 'integer' ? 0 : null)
      const targetValue = this.parseValue(value)
      
      console.log(`[GameModule] Variable: ${variableSlug}`)
      console.log(`[GameModule] API Condition: ${variableSlug} ${operator} ${value}`)
      console.log(`[GameModule] Current: ${currentValue}, Target: ${targetValue}`)
      
      // Evaluate the condition based on operator
      switch (operator) {
        case 'gte':
          return currentValue >= targetValue
        case 'lte':
          return currentValue <= targetValue
        case 'gt':
          return currentValue > targetValue
        case 'lt':
          return currentValue < targetValue
        case 'eq':
          return currentValue == targetValue
        case 'ne':
        case 'neq':
          return currentValue != targetValue
        default:
          console.warn(`[GameModule] Unknown API operator: ${operator}`)
          return false
      }
    }
    
    // Check if it's an inventory condition
    if (condition.target_type === 'object') {
      const { target, operator, value } = condition
      const objectId = target?.id?.toString() // Use the ID as objectId, convert to string for consistency
      
      if (!objectId || !operator || value === undefined) {
        console.warn(`[GameModule] Invalid inventory condition structure:`, condition)
        return false
      }
      
      console.log(`[GameModule] Inventory: ${objectId} (${target?.name || 'unknown'})`)
      console.log(`[GameModule] API Condition: ${objectId} ${operator} ${value}`)
      
      // Get current quantity of the item
      const currentQuantity = this.getInventoryItemQuantity(objectId)
      const targetValue = this.parseValue(value)
      
      console.log(`[GameModule] Current quantity: ${currentQuantity}, Target value: ${targetValue}`)
      
      // Evaluate the condition based on operator
      switch (operator) {
        case 'has':
          return this.hasInventoryItem(objectId)
        case 'not_has':
          return !this.hasInventoryItem(objectId)
        case 'count_gte':
          return this.getInventoryCount() >= targetValue
        case 'count_lte':
          return this.getInventoryCount() <= targetValue
        case 'count_eq':
          return this.getInventoryCount() === targetValue
        case 'eq':
          // For 'eq' operator, check if quantity equals the target value
          return currentQuantity === targetValue
        case 'ne':
        case 'neq':
          return currentQuantity !== targetValue
        case 'gte':
          // Greater than or equal - check if quantity is >= target value
          return currentQuantity >= targetValue
        case 'lte':
          // Less than or equal - check if quantity is <= target value
          return currentQuantity <= targetValue
        case 'gt':
          // Greater than - check if quantity is > target value
          return currentQuantity > targetValue
        case 'lt':
          // Less than - check if quantity is < target value
          return currentQuantity < targetValue
        default:
          console.warn(`[GameModule] Unknown inventory operator: ${operator}`)
          return false
      }
    }
    
    console.warn(`[GameModule] Unknown condition type: ${condition.target_type}`)
    return false
  }

  // Evaluate string condition (legacy format)
  evaluateStringCondition(condition) {
    console.log(`[GameModule] Evaluating string condition: ${condition}`)
    
    // Parse condition (e.g., "score >= 3", "points_visited > 5", "has magic_key", "inventory_count >= 3")
    const operators = ['>=', '<=', '>', '<', '==', '!=', '=']
    let operator = null
    let variable = null
    let value = null
    
    // Find the operator
    for (const op of operators) {
      if (condition.includes(op)) {
        operator = op
        const parts = condition.split(op)
        variable = parts[0].trim()
        value = parts[1].trim()
        break
      }
    }
    
    // Handle inventory conditions without operators (e.g., "has magic_key", "not_has magic_key")
    if (!operator) {
      if (condition.startsWith('has ')) {
        const objectId = condition.substring(4).trim()
        return this.hasInventoryItem(objectId)
      }
      if (condition.startsWith('not_has ')) {
        const objectId = condition.substring(8).trim()
        return !this.hasInventoryItem(objectId)
      }
      if (condition.startsWith('inventory_count ')) {
        const countCondition = condition.substring(16).trim()
        // Parse the count condition (e.g., ">= 3")
        for (const op of operators) {
          if (countCondition.includes(op)) {
            const parts = countCondition.split(op)
            const countValue = this.parseValue(parts[1].trim())
            switch (op) {
              case '>=':
                return this.getInventoryCount() >= countValue
              case '<=':
                return this.getInventoryCount() <= countValue
              case '>':
                return this.getInventoryCount() > countValue
              case '<':
                return this.getInventoryCount() < countValue
              case '==':
              case '=':
                return this.getInventoryCount() == countValue
              case '!=':
                return this.getInventoryCount() != countValue
            }
          }
        }
      }
    }
    
    if (!operator || !variable || value === null) {
      console.warn(`[GameModule] Invalid string condition format: ${condition}`)
      return false
    }
    
    // Get current variable value
    const currentValue = this.getVariableValue(variable)
    const targetValue = this.parseValue(value)
    
    console.log(`[GameModule] String Condition: ${variable} ${operator} ${value}`)
    console.log(`[GameModule] Current: ${currentValue}, Target: ${targetValue}`)
    
    // Evaluate the condition
    switch (operator) {
      case '>=':
        return currentValue >= targetValue
      case '<=':
        return currentValue <= targetValue
      case '>':
        return currentValue > targetValue
      case '<':
        return currentValue < targetValue
      case '==':
      case '=':
        return currentValue == targetValue
      case '!=':
        return currentValue != targetValue
      default:
        console.warn(`[GameModule] Unknown string operator: ${operator}`)
        return false
    }
  }

  // Parse a value (number or string)
  parseValue(value) {
    // Try to parse as number first
    const num = parseFloat(value)
    if (!isNaN(num)) {
      return num
    }
    
    // Return as string (remove quotes if present)
    return value.replace(/^["']|["']$/g, '')
  }

  // Execute actions for a TourPoint
  executeTourPointActions(tourPointData) {
    const pointId = tourPointData.point?.id
    if (!pointId) return
    
    console.log(`[GameModule] Executing actions for TourPoint ${pointId}`)
    
    // Clear pending inventory changes
    this.clearPendingInventoryChanges()
    
    // Get actions from tour point data
    const actions = tourPointData.actions || []
    console.log(`[GameModule] Actions found:`, actions)
    
    actions.forEach(action => {
      this.executeAction(action, pointId)
    })
    
    // Update module data after actions
    moduleManager.setModuleData('game', this.gameState)
  }

  // Execute actions for a specific page
  executePageActions(pageId, pageData) {
    console.log(`[GameModule] Executing actions for page ${pageId}`)
    
    // Clear pending inventory changes at the beginning to prevent showing outdated changes
    this.clearPendingInventoryChanges()
    
    // Get actions from the page only (not falling back to pointData actions)
    const actions = pageData.actions || []
    console.log(`[GameModule] Page actions found:`, actions)
    
    if (actions.length === 0) {
      console.log(`[GameModule] No page-specific actions found for page ${pageId}`)
      return
    }
    
    // Execute each action based on trigger
    actions.forEach(async (action, index) => {
      console.log(`[GameModule] Checking page action ${index + 1}:`, action)
      
      // Create a unique key for this action to track if it's been executed
      const actionKey = `page_${pageId}_action_${index}`
      
      // Get trigger from the correct location in the action structure
      const trigger = action.trigger
      console.log(`[GameModule] Page action trigger:`, trigger)
      
      // Check if action should be triggered
      let shouldExecute = false
      
      if (!trigger) {
        // No trigger specified - execute
        shouldExecute = true
      } else if (trigger === 'every_time') {
        // Execute every time
        shouldExecute = true
      } else if (trigger === 'first_time') {
        // Execute only the first time - check if already executed using GameModule
        shouldExecute = !this.isActionExecuted(actionKey)
      }
      
      if (shouldExecute) {
        console.log(`[GameModule] Executing page action ${index + 1} (trigger: ${trigger || 'default'})`)
        
        if (typeof action === 'object' && action.target_type) {
          // API action structure
          if (action.target_type === 'variable') {
            this.executeVariableAction(action)
          } else if (action.target_type === 'object') {
            this.executeObjectAction(action)
          } else if (action.target_type === 'task') {
            this.executeTaskAction(action)
          } else if (action.target_type === 'connectbox_command') {
            await this.executeConnectboxCommandAction(action)
          } else {
            console.warn(`[GameModule] Unknown action type: ${action.target_type}`)
          }
        } else if (typeof action === 'string') {
          // Legacy string format
          //this.executeStringAction(action)
        } else {
          console.warn(`[GameModule] Unknown action format:`, action)
        }
        
        // Mark action as executed if it's a 'first_time' trigger using GameModule
        if (trigger === 'first_time') {
          this.markActionExecuted(actionKey)
          console.log(`[GameModule] Marked page action ${index + 1} as executed`)
        }
      } else {
        console.log(`[GameModule] Skipping page action ${index + 1} (trigger: ${trigger})`)
      }
    })
    
    // Show batch inventory changes modal if there are any changes
    this.showBatchInventoryChanges()
  }

  // Handle point:before_open event - check conditions and dispatch appropriate response
  handlePointBeforeOpen(data) {
    console.log('=== GAME MODULE HANDLE POINT BEFORE OPEN ===')
    console.log('Point before open event received:', data)
    console.log('[GameModule] Module state when event received:')
    console.log('  - isEnabled:', this.isEnabled)
    console.log('  - moduleManager.isModuleActive("game"):', moduleManager.isModuleActive('game'))
    console.log('  - moduleManager.getModuleState("game"):', moduleManager.getModuleState('game'))
    console.log('[GameModule] This method is being called!')
    
    const { tourPoint, pointId } = data
    if (!tourPoint) {
      console.error('[GameModule] No tour point data in point:before_open event')
      return
    }
    
    console.log(`[GameModule] Checking conditions for TourPoint ${pointId}`)
    
    // Check if TourPoint has conditions
    const conditions = tourPoint.conditions || []
    console.log(`[GameModule] Conditions found:`, conditions)
    
    if (conditions.length === 0) {
      console.log(`[GameModule] TourPoint ${pointId} has no conditions - allowing access`)
      // No conditions, dispatch point:open event
      moduleManager.dispatchEvent('point:open', {
        tourPoint,
        pointId
      })
      return
    }
    
    // Check conditions using current shared variable values
    const allConditionsMet = conditions.every(condition => {
      const isMet = this.evaluateCondition(condition)
      console.log(`[GameModule] Condition "${condition?.target?.name}" for TourPoint ${pointId}: ${isMet}`)
      return isMet
    })
    
    if (allConditionsMet) {
      console.log(`[GameModule] TourPoint ${pointId} unlocked - all conditions met`)
      // Conditions met, dispatch point:open event
      moduleManager.dispatchEvent('point:open', {
        tourPoint,
        pointId
      })
    } else {
      console.log(`[GameModule] TourPoint ${pointId} locked - conditions not met`)
      
      // Get unmet conditions for the lock message
      const unmetConditions = conditions.filter(condition => !this.evaluateCondition(condition))
      
      const message = `This point is locked!\n\nRequirements:\n${unmetConditions.join('\n')}\n\nCurrent game state:\nScore: ${this.getScore()}\n`
      
      // Dispatch point:locked event
      moduleManager.dispatchEvent('point:locked', {
        pointId,
        message,
        unmetConditions,
        currentState: this.gameState
      })
    }
  }

  // Quiz answer methods
  setQuizAnswer(pointId, questionId, answer) {
    console.log(`[GameModule] Setting quiz answer: point ${pointId}, question ${questionId}, answer ${answer}`)
    
    if (!this.gameState.quiz_answers[pointId]) {
      this.gameState.quiz_answers[pointId] = {}
    }
    
    this.gameState.quiz_answers[pointId][questionId] = answer
    console.log(`[GameModule] Quiz answers for point ${pointId}:`, this.gameState.quiz_answers[pointId])
    
    // Update module data
    moduleManager.setModuleData('game', this.gameState)
  }
  
  getQuizAnswer(pointId, questionId) {
    const answer = this.gameState.quiz_answers[pointId]?.[questionId]
    console.log(`[GameModule] Getting quiz answer: point ${pointId}, question ${questionId}, answer ${answer}`)
    return answer || null
  }
  
  getPointQuizAnswers(pointId) {
    const answers = this.gameState.quiz_answers[pointId] || {}
    console.log(`[GameModule] Getting all quiz answers for point ${pointId}:`, answers)
    return answers
  }
  
  hasQuizAnswers(pointId) {
    const hasAnswers = this.gameState.quiz_answers[pointId] && Object.keys(this.gameState.quiz_answers[pointId]).length > 0
    console.log(`[GameModule] Point ${pointId} has quiz answers:`, hasAnswers)
    return hasAnswers
  }
  
  clearQuizAnswers(pointId) {
    if (this.gameState.quiz_answers[pointId]) {
      delete this.gameState.quiz_answers[pointId]
      console.log(`[GameModule] Cleared quiz answers for point ${pointId}`)
    }
  }

  // Game completion methods (always stores boolean)
  setGameCompletion(gameId, completed = true) {
    console.log(`[GameModule] Setting game completion: gameId ${gameId}, completed:`, completed)
    
    this.gameState.game_completions[gameId] = !!completed
    console.log(`[GameModule] Game completions updated:`, this.gameState.game_completions)
    
    // Update module data to persist the change
    moduleManager.setModuleData('game', this.gameState)
  }
  
  getGameCompletion(gameId) {
    const completion = this.gameState.game_completions[gameId]
    console.log(`[GameModule] Getting game completion: gameId ${gameId}, completed:`, completion)
    // Always return boolean
    return completion === true
  }
  
  getAllGameCompletions() {
    return this.gameState.game_completions || {}
  }
  
  hasGameCompletion(gameId) {
    return this.gameState.game_completions[gameId] === true
  }
  
  clearGameCompletion(gameId) {
    if (this.gameState.game_completions[gameId]) {
      delete this.gameState.game_completions[gameId]
      console.log(`[GameModule] Cleared game completion for gameId ${gameId}`)
      // Update module data to persist the change
      moduleManager.setModuleData('game', this.gameState)
    }
  }

  // Check if an action has been executed
  isActionExecuted(actionKey) {
    return this.gameState.executedActions.has(actionKey)
  }

  // Mark an action as executed
  markActionExecuted(actionKey) {
    this.gameState.executedActions.add(actionKey)
    console.log(`[GameModule] Marked action as executed: ${actionKey}`)
    
    // Update module data to persist the change
    moduleManager.setModuleData('game', this.gameState)
  }

  // Get all executed actions (for debugging)
  getExecutedActions() {
    return Array.from(this.gameState.executedActions)
  }

  // Check if a point should be shown (visible in the points list)
  shouldShowPoint(pointData) {
    console.log(`[GameModule] Checking if point should be shown:`, pointData)
    
    const pointId = pointData.point?.id || pointData.data?.point?.id
    if (!pointId) {
      console.log(`[GameModule] No point ID found, showing point by default`)
      return true
    }
    
    // Check if GameModule is active
    if (!this.isEnabled) {
      console.log(`[GameModule] GameModule not enabled, showing point by default`)
      console.log(`[GameModule] Debug info - this.isEnabled: ${this.isEnabled}`)
      console.log(`[GameModule] Debug info - moduleManager.isModuleActive('game'): ${moduleManager.isModuleActive('game')}`)
      console.log(`[GameModule] Debug info - moduleManager.getModuleState('game'):`, moduleManager.getModuleState('game'))
      console.log(`[GameModule] Debug info - this instance:`, this)
      return true
    }
    
    // Check if game state is properly initialized
    if (!this.gameState || !this.gameState.variables) {
      console.log(`[GameModule] Game state not initialized, showing point by default`)
      return true
    }
    
    // Get visibility conditions from the correct location in the data structure
    let visibilityConditions = []
    
    if (pointData.visibility_conditions) {
      visibilityConditions = pointData.visibility_conditions
    } else if (pointData.show_conditions) {
      visibilityConditions = pointData.show_conditions // Fallback for backward compatibility
    } else if (pointData.data?.visibility_conditions) {
      visibilityConditions = pointData.data.visibility_conditions
    } else if (pointData.data?.show_conditions) {
      visibilityConditions = pointData.data.show_conditions // Fallback for backward compatibility
    } else if (pointData.data?.tour_point?.visibility_conditions) {
      visibilityConditions = pointData.data.tour_point.visibility_conditions
    } else if (pointData.data?.tour_point?.show_conditions) {
      visibilityConditions = pointData.data.tour_point.show_conditions // Fallback for backward compatibility
    }
    
    console.log(`[GameModule] Visibility conditions found for point ${pointId}:`, visibilityConditions)
    
    // If no visibility conditions, the point is visible by default
    if (!visibilityConditions || visibilityConditions.length === 0) {
      console.log(`[GameModule] Point ${pointId} has no visibility conditions - showing by default`)
      return true
    }
    
    // Check all visibility conditions
    const allVisibilityConditionsMet = visibilityConditions.every(condition => {
      try {
        const isMet = this.evaluateCondition(condition)
        console.log(`[GameModule] Visibility condition "${condition?.target?.name}" for point ${pointId}: ${isMet}`)
        return isMet
      } catch (error) {
        console.error(`[GameModule] Error evaluating visibility condition for point ${pointId}:`, error)
        // If condition evaluation fails, show the point by default
        return true
      }
    })
    
    console.log(`[GameModule] Point ${pointId} visibility conditions met: ${allVisibilityConditionsMet}`)
    return allVisibilityConditionsMet
  }

  // Check if a point should be unlocked (accessible when clicked)
  shouldUnlockPoint(pointData) {
    console.log(`[GameModule] Checking if point should be unlocked:`, pointData)
    
    const pointId = pointData.point?.id || pointData.data?.point?.id
    if (!pointId) {
      console.log(`[GameModule] No point ID found, unlocking point by default`)
      return true
    }
    
    // Check if GameModule is active
    if (!this.isEnabled) {
      console.log(`[GameModule] GameModule not enabled, unlocking point by default`)
      return true
    }
    
    // Check if game state is properly initialized
    if (!this.gameState || !this.gameState.variables) {
      console.log(`[GameModule] Game state not initialized, unlocking point by default`)
      return true
    }
    
    // Get unlock conditions from the correct location in the data structure
    let unlockConditions = []
    
    if (pointData.unlock_conditions) {
      unlockConditions = pointData.unlock_conditions
    } else if (pointData.conditions) {
      unlockConditions = pointData.conditions // Legacy: use conditions as unlock_conditions
    } else if (pointData.data?.unlock_conditions) {
      unlockConditions = pointData.data.unlock_conditions
    } else if (pointData.data?.conditions) {
      unlockConditions = pointData.data.conditions // Legacy: use conditions as unlock_conditions
    } else if (pointData.data?.tour_point?.unlock_conditions) {
      unlockConditions = pointData.data.tour_point.unlock_conditions
    } else if (pointData.data?.tour_point?.conditions) {
      unlockConditions = pointData.data.tour_point.conditions // Legacy: use conditions as unlock_conditions
    }
    
    console.log(`[GameModule] Unlock conditions found for point ${pointId}:`, unlockConditions)
    
    // If no unlock conditions, the point is unlocked by default
    if (!unlockConditions || unlockConditions.length === 0) {
      console.log(`[GameModule] Point ${pointId} has no unlock conditions - unlocking by default`)
      return true
    }
    
    // Check all unlock conditions
    const allUnlockConditionsMet = unlockConditions.every(condition => {
      try {
        const isMet = this.evaluateCondition(condition)
        console.log(`[GameModule] Unlock condition "${condition?.target?.name}" for point ${pointId}: ${isMet}`)
        return isMet
      } catch (error) {
        console.error(`[GameModule] Error evaluating unlock condition for point ${pointId}:`, error)
        // If condition evaluation fails, unlock the point by default
        return true
      }
    })
    
    console.log(`[GameModule] Point ${pointId} unlock conditions met: ${allUnlockConditionsMet}`)
    return allUnlockConditionsMet
  }

  // Get unmet conditions for a point (for display in lock messages)
  getUnmetConditions(pointData, conditionType = 'unlock') {
    const pointId = pointData.point?.id || pointData.data?.point?.id
    if (!pointId) return []
    
    let conditions = []
    
    if (conditionType === 'show') {
      if (pointData.visibility_conditions) {
        conditions = pointData.visibility_conditions
      } else if (pointData.show_conditions) {
        conditions = pointData.show_conditions // Fallback for backward compatibility
      } else if (pointData.data?.visibility_conditions) {
        conditions = pointData.data.visibility_conditions
      } else if (pointData.data?.show_conditions) {
        conditions = pointData.data.show_conditions // Fallback for backward compatibility
      } else if (pointData.data?.tour_point?.visibility_conditions) {
        conditions = pointData.data.tour_point.visibility_conditions
      } else if (pointData.data?.tour_point?.show_conditions) {
        conditions = pointData.data.tour_point.show_conditions // Fallback for backward compatibility
      }
    } else {
      // unlock conditions
      if (pointData.unlock_conditions) {
        conditions = pointData.unlock_conditions
      } else if (pointData.conditions) {
        conditions = pointData.conditions // Legacy: use conditions as unlock_conditions
      } else if (pointData.data?.unlock_conditions) {
        conditions = pointData.data.unlock_conditions
      } else if (pointData.data?.conditions) {
        conditions = pointData.data.conditions // Legacy: use conditions as unlock_conditions
      } else if (pointData.data?.tour_point?.unlock_conditions) {
        conditions = pointData.data.tour_point.unlock_conditions
      } else if (pointData.data?.tour_point?.conditions) {
        conditions = pointData.data.tour_point.conditions // Legacy: use conditions as unlock_conditions
      }
    }
    
    return conditions.filter(condition => !this.evaluateCondition(condition))
  }

  // Process all points to determine visibility and unlock status
  processAllPoints(pointsData) {
    console.log(`[GameModule] Processing all points for visibility and unlock status`)
    console.log(`[GameModule] Module state check:`)
    console.log(`  - this.isEnabled: ${this.isEnabled}`)
    console.log(`  - moduleManager.isModuleActive('game'): ${moduleManager.isModuleActive('game')}`)
    console.log(`  - moduleManager.getModuleState('game'):`, moduleManager.getModuleState('game'))
    console.log(`  - Points data received:`, pointsData ? Object.keys(pointsData).length : 'null')
    
    // Check if module is fully enabled and loaded
    if (!this.isEnabled || !moduleManager.isModuleActive('game')) {
      console.log(`[GameModule] GameModule not fully enabled, skipping point processing`)
      console.log(`[GameModule] Returning points as-is for now`)
      return pointsData
    }
    
    // Additional check to ensure game state is initialized
    if (!this.gameState || !this.gameState.variables) {
      console.log(`[GameModule] Game state not fully initialized, skipping point processing`)
      console.log(`[GameModule] Current game state:`, this.gameState)
      return pointsData
    }
    
    // Check if pointsData is valid
    if (!pointsData || typeof pointsData !== 'object') {
      console.log(`[GameModule] Invalid points data received:`, pointsData)
      return pointsData
    }
    
    console.log(`[GameModule] GameModule is fully enabled and ready, processing points`)
    
    const processedPoints = {}
    
    Object.entries(pointsData).forEach(([pointId, pointData]) => {
      console.log(`[GameModule] Processing point ${pointId}`)
      
      const shouldShow = this.shouldShowPoint(pointData)
      const shouldUnlock = this.shouldUnlockPoint(pointData)
      
      console.log(`[GameModule] Point ${pointId}: show=${shouldShow}, unlock=${shouldUnlock}`)
      
      // Add visibility and unlock status to point data
      const processedPoint = {
        ...pointData,
        _gameModule: {
          visible: shouldShow,
          unlocked: shouldUnlock,
          unmetVisibilityConditions: shouldShow ? [] : this.getUnmetConditions(pointData, 'show'),
          unmetUnlockConditions: shouldUnlock ? [] : this.getUnmetConditions(pointData, 'unlock')
        }
      }
      
      processedPoints[pointId] = processedPoint
    })
    
    console.log(`[GameModule] Processed ${Object.keys(processedPoints).length} points`)
    return processedPoints
  }
}

// Create and register the module
const gameModule = new GameModule()
moduleManager.registerModule('game', gameModule)

export default gameModule 